/**
 * The estimate/proposal DOCUMENT-TEMPLATE contract — the block vocabulary a
 * tenant arranges to brand and lay out the customer-facing estimate, mirroring
 * ServiceTitan's Template Manager. Shared verbatim by the frontend builder/
 * renderer and the operations seed/render so both sides agree on one schema.
 *
 * This is PRESENTATION only. It never carries pricing or what-is-sold data
 * (that stays on the Estimate / EstimateTemplate) — a block references live
 * estimate values by key, it does not embed them.
 *
 * Runtime values live here (`.cjs`); the types that describe them live in the
 * sibling `.d.cts`. See `form-items.cjs` for the same split.
 */

/**
 * Content blocks render document material from the estimate. Order is the
 * canonical top-to-bottom order a fresh template lists them in.
 */
const ESTIMATE_DOC_CONTENT_BLOCKS = [
  "logo",
  "companyInfo",
  "billTo",
  "jobAddress",
  "estimateDetails",
  // Customer-facing scope-of-work prose (bound to the estimate's `scopeOfWork`).
  "scopeOfWork",
  "itemGroups",
  "totals",
  "deposit",
  // Join-and-save membership offer card (bound to the estimate's membership
  // snapshot); renders nothing when the estimate carries no offer.
  "membershipOffer",
  // ── INVOICE-only content blocks ──────────────────────────────────────────
  // Invoice header facts: Invoice #, invoice date, DUE DATE, status.
  "invoiceDetails",
  // Amount paid / credits applied / BALANCE DUE + a Paid/Void status stamp.
  "balanceSummary",
  // The payments recorded against the invoice.
  "paymentHistory",
  // Where the customer remits payment (company remit-to address).
  "remitTo",
  // Invoice custom-field values marked customer-visible — the "Additional
  // Details" list the classic invoice PDF renders (label: value rows).
  "additionalDetails",
  // Live document fields (bound like itemGroups/totals): `notes` prints the
  // document's own notes, `terms` its terms & conditions text.
  "notes",
  "terms",
  // Customer-supplied / job photos attached to the estimate.
  "attachments",
  // Template-authored prose (author types the content, not bound to a field).
  "authText",
  "ackText",
  "richText",
];

/**
 * Interaction slots are positioned by the template but OWNED BY THE APP: the
 * author can move or hide a slot, never change what it does (approve, take a
 * payment, sign). They render nothing in the PDF (see `slotSuppressedInPdf`).
 */
const ESTIMATE_DOC_SLOT_BLOCKS = [
  "approveDecline",
  "payDeposit",
  "signature",
  "goodBetterBest",
  // INVOICE-only: the customer "Pay invoice" action (app-owned, like payDeposit).
  "payInvoice",
];

/** Item/line field keys a template can hide or relabel (ST "hide qty/price"). */
const ESTIMATE_DOC_ITEM_FIELDS = [
  "description",
  "quantity",
  "unitPrice",
  "tax",
  "lineAmount",
  "itemImage",
];

const ESTIMATE_DOC_TYPES = ["ESTIMATE", "PROPOSAL", "INVOICE"];
const ESTIMATE_DOC_TEMPLATE_STATUSES = ["DRAFT", "PUBLISHED"];

/** Slots never draw in the PDF — a printed estimate has no "Approve" button. */
const PDF_SUPPRESSED_SLOT_BLOCKS = [
  "approveDecline",
  "payDeposit",
  "signature",
  "payInvoice",
];

const DEFAULT_DOCUMENT_BRANDING = {
  logoKey: "",
  logoUrl: null,
  logoDataUri: null,
  primaryColor: "#1F2937",
  secondaryColor: "#4B5563",
  accentColor: "#C0561F",
  bodyFont: "Inter",
  headingFont: "Inter",
  contactBlock: "",
  tagline: "",
  licenseNumber: "",
  regulatoryNotice: "",
  displayName: "",
  address: null,
  phone: "",
  email: "",
  taxRegistrationNumber: "",
};

const DEFAULT_ESTIMATE_DOC_PAGE_SETUP = {
  paper: "LETTER",
  orientation: "portrait",
  marginsMm: { top: 18, right: 16, bottom: 18, left: 16 },
};

const DEFAULT_ESTIMATE_DOC_HEADER = {
  enabled: false,
  showLogo: true,
  showEstimateNumber: true,
};

const DEFAULT_ESTIMATE_DOC_FOOTER = {
  enabled: false,
  showPageNumber: true,
  showContact: true,
};

module.exports = {
  ESTIMATE_DOC_CONTENT_BLOCKS,
  ESTIMATE_DOC_SLOT_BLOCKS,
  ESTIMATE_DOC_ITEM_FIELDS,
  ESTIMATE_DOC_TYPES,
  ESTIMATE_DOC_TEMPLATE_STATUSES,
  PDF_SUPPRESSED_SLOT_BLOCKS,
  DEFAULT_DOCUMENT_BRANDING,
  DEFAULT_ESTIMATE_DOC_PAGE_SETUP,
  DEFAULT_ESTIMATE_DOC_HEADER,
  DEFAULT_ESTIMATE_DOC_FOOTER,
};
